import { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { Chart } from 'chart.js';
import {
    createStackedVerticalBarChart,
    StackedVerticalBarChartData,
    StackedVerticalBarChartTooltipFooterFormatter,
    StackedVerticalBarChartTooltipLabelFormatter,
} from './util/StackedVerticalBarChart';
import { getChartDisplayConfig } from '@/features/charts/useBarChartScrollContainer';
import { useSmUpViewport } from '@/hooks/useSmUpViewport';
import type { BarChartViewMode } from '@/features/charts/BarChartViewModeToggle';
import { BarChartScrollContainer } from '@/features/charts/BarChartScrollContainer';
import chartIcon from '@/assets/images/sidebar/analysis_icon.png';
import { useTranslation } from 'react-i18next';

export interface StackedVerticalBarChartHandle {
    toggleDataset: (index: number) => void;
    isDatasetVisible: (index: number) => boolean;
}

type StackedVerticalBarChartInnerProps = {
    data: StackedVerticalBarChartData;
    resolveColor?: (label: string, index: number) => Promise<string | undefined> | string | undefined;
    tooltipLabelFormatter?: StackedVerticalBarChartTooltipLabelFormatter;
    tooltipFooterFormatter?: StackedVerticalBarChartTooltipFooterFormatter;
    yAxisMax?: number;
    yAxisStepSize?: number;
    yAxisSuggestedMax?: number;
    formatYAxisTick?: (value: number) => string;
    chartBarLabelLayout: 'fit' | 'expand';
    innerWidthPx: number;
    onChartUpdate?: () => void;
};

const generateFallbackColors = (count: number): string[] =>
    Array.from({ length: count }, (_, index) => {
        const hue = (index * 360) / Math.max(count, 1);
        return `hsl(${hue}, 60%, 72%)`;
    });

const StackedVerticalBarChartInner = forwardRef<StackedVerticalBarChartHandle, StackedVerticalBarChartInnerProps>(
    (
        {
            data,
            resolveColor,
            tooltipLabelFormatter,
            tooltipFooterFormatter,
            yAxisMax,
            yAxisStepSize,
            yAxisSuggestedMax,
            formatYAxisTick,
            chartBarLabelLayout,
            innerWidthPx,
            onChartUpdate,
        },
        ref,
    ) => {
        const canvasRef = useRef<HTMLCanvasElement | null>(null);
        const chartRef = useRef<Chart<'bar'> | null>(null);

        useImperativeHandle(ref, () => ({
            toggleDataset: (index: number) => {
                const chart = chartRef.current;
                if (!chart) return;
                const meta = chart.getDatasetMeta(index);
                const currentlyVisible = chart.isDatasetVisible(index);
                meta.hidden = currentlyVisible ? true : false;
                chart.update();
                onChartUpdate?.();
            },
            isDatasetVisible: (index: number) => {
                const chart = chartRef.current;
                if (!chart) return true;
                return chart.isDatasetVisible(index);
            },
        }));

        useEffect(() => {
            if (!canvasRef.current) return;

            chartRef.current?.destroy();

            if (!data || !data.datasets.length) return;

            const ctx = canvasRef.current.getContext('2d');
            if (!ctx) return;

            let isMounted = true;

            const run = async () => {
                const fallbackColors = generateFallbackColors(Math.max(data.datasets.length, 1));

                const enrichedDatasets = await Promise.all(
                    data.datasets.map(async (dataset, index) => {
                        if (dataset.backgroundColor) {
                            return dataset;
                        }
                        const targetKey = dataset.id ?? dataset.label;
                        let resolved: string | undefined;
                        try {
                            resolved = (await resolveColor?.(targetKey, index)) ?? undefined;
                        } catch {
                            resolved = undefined;
                        }
                        return {
                            ...dataset,
                            backgroundColor: resolved ?? fallbackColors[index % fallbackColors.length],
                        };
                    }),
                );

                const chart = createStackedVerticalBarChart(
                    ctx,
                    {
                        ...data,
                        datasets: enrichedDatasets,
                    },
                    {
                        yAxisMax,
                        yAxisStepSize,
                        yAxisSuggestedMax,
                        formatYAxisTick,
                        formatTooltipLabel: tooltipLabelFormatter,
                        formatTooltipFooter: tooltipFooterFormatter,
                        barLabelLayout: chartBarLabelLayout,
                    },
                );

                if (!isMounted) {
                    chart?.destroy();
                    return;
                }
                chartRef.current = chart ?? null;
                onChartUpdate?.(); // 初期描画完了を通知
            };

            run();

            return () => {
                isMounted = false;
                chartRef.current?.destroy();
                chartRef.current = null;
            };
        }, [
            data,
            resolveColor,
            tooltipFooterFormatter,
            tooltipLabelFormatter,
            yAxisMax,
            yAxisStepSize,
            yAxisSuggestedMax,
            formatYAxisTick,
            chartBarLabelLayout,
            // onChartUpdate は依存配列に含めない（無限ループ防止）
        ]);

        useEffect(() => {
            chartRef.current?.resize();
        }, [innerWidthPx, chartBarLabelLayout]);

        return <canvas ref={canvasRef} className="block h-full w-full max-w-none" />;
    },
);

StackedVerticalBarChartInner.displayName = 'StackedVerticalBarChartInner';

// --- 凡例コンポーネント ---

type StackedBarChartLegendProps = {
    data: StackedVerticalBarChartData;
    resolveColor?: (label: string, index: number) => Promise<string | undefined> | string | undefined;
    chartHandle: StackedVerticalBarChartHandle | null;
    legendOpen: boolean;
    onToggleLegend: () => void;
    legendAnchorRef?: React.RefObject<HTMLElement | null>;
    hideLegendButton?: boolean;
    refreshTick: number; // チャート更新時に再描画するためのトリガー
};

const StackedBarChartLegend = ({
    data,
    resolveColor,
    chartHandle,
    legendOpen,
    onToggleLegend,
    legendAnchorRef,
    hideLegendButton,
    refreshTick,
}: StackedBarChartLegendProps) => {
    const { t } = useTranslation('analysis');
    const [legendColors, setLegendColors] = useState<string[]>([]);
    const legendButtonRef = useRef<HTMLButtonElement | null>(null);
    const [positionTick, setPositionTick] = useState(0);

    useEffect(() => {
        if (!legendOpen) return;
        const handler = () => setPositionTick((v) => v + 1);
        window.addEventListener('scroll', handler, true);
        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('scroll', handler, true);
            window.removeEventListener('resize', handler);
        };
    }, [legendOpen]);

    useEffect(() => {
        let cancelled = false;
        const buildLegendColors = async () => {
            if (!data || !data.datasets.length) {
                setLegendColors([]);
                return;
            }
            const fallbackColors = generateFallbackColors(Math.max(data.datasets.length, 1));
            const colors = await Promise.all(
                data.datasets.map(async (dataset, index) => {
                    if (dataset.backgroundColor) {
                        return String(dataset.backgroundColor);
                    }
                    const key = dataset.id ?? dataset.label;
                    try {
                        const resolved = await resolveColor?.(key, index);
                        return resolved ?? fallbackColors[index % fallbackColors.length];
                    } catch {
                        return fallbackColors[index % fallbackColors.length];
                    }
                }),
            );
            if (!cancelled) setLegendColors(colors);
        };
        buildLegendColors();
        return () => {
            cancelled = true;
        };
    }, [data, resolveColor]);

    const renderLegendList = (maxH?: number) => (
        <div
            className="w-56 sm:w-80 rounded-md border border-gray-200 bg-white/95 shadow overflow-auto"
            style={maxH ? { maxHeight: `${maxH}px` } : { maxHeight: '60vh' }}
        >
            <ul className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                {data.datasets.map((ds, idx) => {
                    void refreshTick;
                    const color = legendColors[idx] ?? '#cccccc';
                    // refreshTick で親から再描画され、表示状態を取り直す
                    const visible = chartHandle?.isDatasetVisible(idx) ?? true;
                    const key = ds.id ?? ds.label ?? String(idx);
                    const labelText = (ds.label?.trim()?.length ?? 0) > 0 ? ds.label : t('chart.untitled');
                    return (
                        <li
                            key={key}
                            className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-50 rounded border border-gray-200"
                            onClick={() => chartHandle?.toggleDataset(idx)}
                            title={labelText}
                        >
                            <span
                                className="inline-block h-3 w-3 rounded"
                                style={{ backgroundColor: color }}
                                aria-hidden="true"
                            />
                            <span className={`text-sm truncate ${visible ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                                {labelText}
                            </span>
                            <button
                                type="button"
                                role="checkbox"
                                aria-checked={visible}
                                aria-label={`toggle-${labelText}`}
                                className="ms-auto h-6 w-6 flex items-center justify-center"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    chartHandle?.toggleDataset(idx);
                                }}
                            >
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 20 20"
                                    className={visible ? 'text-gray-800' : 'text-gray-300'}
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M5 10l3 3 7-7"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );

    return (
        <div className="flex items-center gap-2">
            {!hideLegendButton && (
                <div className="relative inline-block">
                    <button
                        ref={legendButtonRef}
                        type="button"
                        className="px-2 py-1 text-xs rounded bg-white/80 hover:bg-white shadow border border-gray-200 flex items-center gap-1"
                        onClick={onToggleLegend}
                        aria-pressed={legendOpen}
                    >
                        <span>{t('chart.legendColors', { defaultValue: '項目' })}</span>
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                            className={`transition-transform duration-150 ${legendOpen ? '-rotate-90' : ''}`}
                            aria-hidden="true"
                        >
                            <path
                                d="M7 5l5 5-5 5"
                                stroke="#374151"
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                    {legendOpen && (
                        <>
                            <div
                                style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'transparent' }}
                                onClick={onToggleLegend}
                                aria-hidden="true"
                            />
                            <div className="absolute right-0 bottom-full mb-2 z-[9999]">
                                {renderLegendList()}
                            </div>
                        </>
                    )}
                </div>
            )}
            {/* 外部アンカー用のポータル描画 */}
            {legendOpen &&
                hideLegendButton &&
                createPortal(
                    (() => {
                        const anchorEl = (legendAnchorRef && legendAnchorRef.current) || null;
                        if (!anchorEl) return null as any;
                        const rect = anchorEl.getBoundingClientRect();
                        const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
                        const spaceAbove = rect.top - 12;
                        const spaceBelow = viewportH - rect.bottom - 12;
                        const placeAbove = spaceAbove >= 180 || spaceAbove >= spaceBelow;
                        const maxH = Math.max(
                            140,
                            Math.min(Math.floor(viewportH * 0.6), Math.floor(placeAbove ? spaceAbove : spaceBelow)),
                        );
                        const scrollX = typeof window !== 'undefined' ? window.scrollX : 0;
                        const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;
                        const commonPos = { position: 'absolute' as const, left: rect.right + scrollX, zIndex: 9999 };
                        const style = placeAbove
                            ? { ...commonPos, top: rect.top + scrollY, transform: 'translate(-100%, calc(-100% - 12px))' }
                            : { ...commonPos, top: rect.bottom + scrollY + 12, transform: 'translateX(-100%)' };
                        return (
                            <>
                                <div
                                    style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'transparent' }}
                                    onClick={onToggleLegend}
                                    aria-hidden="true"
                                />
                                <div style={style} data-pos-tick={positionTick}>
                                    {renderLegendList(maxH)}
                                </div>
                            </>
                        );
                    })(),
                    document.body,
                )}
        </div>
    );
};

// --- メインコンポーネント ---

export type StackedVerticalBarChartProps = {
    data: StackedVerticalBarChartData;
    resolveColor?: (label: string, index: number) => Promise<string | undefined> | string | undefined;
    tooltipLabelFormatter?: StackedVerticalBarChartTooltipLabelFormatter;
    tooltipFooterFormatter?: StackedVerticalBarChartTooltipFooterFormatter;
    yAxisMax?: number;
    yAxisStepSize?: number;
    yAxisSuggestedMax?: number;
    formatYAxisTick?: (value: number) => string;
    noDataMessage?: string;
    onSwitchToDonut?: () => void;
    hideSwitchButton?: boolean;
    hideLegendButton?: boolean;
    legendOpen?: boolean;
    onToggleLegend?: () => void;
    legendAnchorRef?: React.RefObject<HTMLElement | null>;
    barViewMode?: BarChartViewMode;
};

const StackedVerticalBarChart = ({
    data,
    resolveColor,
    tooltipLabelFormatter,
    tooltipFooterFormatter,
    yAxisMax,
    yAxisStepSize,
    yAxisSuggestedMax,
    formatYAxisTick,
    noDataMessage,
    onSwitchToDonut,
    hideSwitchButton,
    hideLegendButton,
    legendOpen,
    onToggleLegend,
    legendAnchorRef,
    barViewMode: barViewModeProp,
}: StackedVerticalBarChartProps) => {
    const { t } = useTranslation('analysis');
    const chartHandleRef = useRef<StackedVerticalBarChartHandle>(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const isSmUpViewport = useSmUpViewport();

    const labelCount = data?.labels?.length ?? 0;
    const viewMode = barViewModeProp ?? 'expand';
    
    const config = useMemo(() => {
        return getChartDisplayConfig('ratio', labelCount, isSmUpViewport, viewMode);
    }, [isSmUpViewport, labelCount, viewMode]);

    const { scrollLayout, showBarLabels } = config;
    const chartBarLabelLayout = showBarLabels ? 'expand' : 'fit';

    const [internalShowLegend, setInternalShowLegend] = useState(false);
    const effectiveShowLegend = legendOpen ?? internalShowLegend;
    const handleToggleLegend = () => {
        if (onToggleLegend) {
            onToggleLegend();
        } else {
            setInternalShowLegend((v) => !v);
        }
    };

    const handleChartUpdate = () => {
        setRefreshTick((v) => v + 1);
    };

    const noDataLabel = noDataMessage ?? t('chart.noData');

    if (!data || !data.datasets.length) {
        return (
            <div className="flex h-full flex-col items-center justify-center">
                <img src={chartIcon} alt={noDataLabel} className="w-20 h-20 image-protection" />
                <p className="mt-4 text-xl">{noDataLabel}</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col">
            <div className="relative flex items-center justify-end gap-2 px-2 pt-2 pb-1">
                <StackedBarChartLegend
                    data={data}
                    resolveColor={resolveColor}
                    chartHandle={chartHandleRef.current}
                    legendOpen={effectiveShowLegend}
                    onToggleLegend={handleToggleLegend}
                    legendAnchorRef={legendAnchorRef}
                    hideLegendButton={hideLegendButton}
                    refreshTick={refreshTick}
                />
                {!hideSwitchButton && (
                    <button
                        type="button"
                        aria-label="switch-to-donut"
                        className="px-2 py-1 text-xs rounded bg-white/80 hover:bg-white shadow border border-gray-200"
                        onClick={() => onSwitchToDonut?.()}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2v10l8.49 4.9A10 10 0 1 1 12 2z" fill="#9CA3AF" />
                            <path d="M14 12h8A10 10 0 0 1 12 22v-8a2 2 0 0 0 2-2z" fill="#D1D5DB" />
                        </svg>
                    </button>
                )}
            </div>

            <BarChartScrollContainer labelCount={labelCount} scrollLayout={scrollLayout}>
                {({ innerWidthPx }) => (
                    <StackedVerticalBarChartInner
                        ref={chartHandleRef}
                        data={data}
                        resolveColor={resolveColor}
                        tooltipLabelFormatter={tooltipLabelFormatter}
                        tooltipFooterFormatter={tooltipFooterFormatter}
                        yAxisMax={yAxisMax}
                        yAxisStepSize={yAxisStepSize}
                        yAxisSuggestedMax={yAxisSuggestedMax}
                        formatYAxisTick={formatYAxisTick}
                        chartBarLabelLayout={chartBarLabelLayout}
                        innerWidthPx={innerWidthPx}
                        onChartUpdate={handleChartUpdate}
                    />
                )}
            </BarChartScrollContainer>
        </div>
    );
};

export default StackedVerticalBarChart;
